import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Zap, Search } from 'lucide-react';
import HeaderLogo from './HeaderLogo';

export default function LandingPage() {
  // Define features array at the top
  const features = [
    { icon: Search, title: 'Instant Search', description: 'Get results in seconds' },
    { icon: Shield, title: 'Risk Analysis', description: 'AI-powered conflict detection' },
    { icon: Zap, title: 'Real-time Updates', description: 'Live database monitoring' }
  ];

  // State declarations
  const [trademark, setTrademark] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [agreedToComms, setAgreedToComms] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);

  const placeholders = [
    "Enter your brand name...",
    "Enter your domain name...",
    "Enter your trademark name..."
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((current) => (current + 1) % placeholders.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setShowEmailForm(true);
    }, 2000);
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-gradient-to-b from-white to-gray-50 pt-8">
      {/* Header Section */}
      <header className="w-full border-b border-gray-100 bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 py-6 flex justify-center items-center">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
            Trademarking.ai
          </h1>
        </div>
      </header>

      <div className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="bg-blue-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium animate-float">
                Powered by AI
              </div>
            </div>
            <p className="text-lg md:text-xl text-gray-600">
              Instant trademark availability search and analysis
            </p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-3xl shadow-[0_10px_40px_-15px_rgba(0,0,0,0.2)] p-6 md:p-12 mb-12">
            <div className="w-full max-w-2xl mx-auto">
              {!showEmailForm ? (
                <form onSubmit={handleSubmit}>
                  <input
                    type="text"
                    placeholder={placeholders[placeholderIndex]}
                    value={trademark}
                    onChange={(e) => setTrademark(e.target.value)}
                    className="w-full p-4 md:p-6 text-lg rounded-[16px] border-2 border-gray-400 mb-6 shadow-sm transition-all duration-300 focus:border-blue-500 animate-border-pulse"
                    required
                  />
                  
                  <button 
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-lg md:text-xl font-medium p-4 md:p-6 rounded-[16px] mb-8 shadow-lg disabled:opacity-50 hover:opacity-90 transition-all transform hover:scale-[1.02] flex items-center justify-center"
                    disabled={isGenerating}
                  >
                    {isGenerating ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-4 border-white border-t-transparent mr-3"></div>
                        <span>Generating Report...</span>
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="bg-blue-50 p-6 rounded-[16px] text-center">
                    <h3 className="text-xl text-blue-700 font-medium mb-2">Your Report is being Generated!</h3>
                    <p className="text-blue-600">Enter your email below to receive your trademark report for "{trademark}"</p>
                  </div>

                  <form action="https://formspree.io/f/myzkpzev" method="POST" className="space-y-6">
                    <input type="hidden" name="trademark" value={trademark} />
                    <label>
                      <input
                        type="email"
                        name="email"
                        placeholder="Your Email"
                        className="w-full p-4 md:p-6 text-lg rounded-[16px] border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </label>
                    <input type="hidden" name="message" value={`Trademark Report Request for: ${trademark}`} />
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="communications-consent"
                        checked={agreedToComms}
                        onChange={(e) => setAgreedToComms(e.target.checked)}
                        className="mt-1.5"
                        required
                      />
                      <label htmlFor="communications-consent" className="text-sm text-gray-600">
                        I agree to receive communications about my trademark report and related services
                      </label>
                    </div>
                    <button 
                      type="submit" 
                      className="w-full bg-green-600 hover:bg-green-700 text-white text-lg md:text-xl font-medium p-4 md:p-6 rounded-[16px] shadow-lg transition-all transform hover:scale-[1.02] flex items-center justify-center"
                      disabled={!agreedToComms}
                    >
                      Send My Report
                    </button>
                    <button 
                      type="button"
                      onClick={() => {
                        setShowEmailForm(false);
                        setTrademark('');
                      }}
                      className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-lg font-medium p-4 rounded-[16px] transition-all"
                    >
                      Start New Search
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              {features.map((feature, index) => (
                <div key={index} className="p-6 rounded-[16px] bg-gray-50 hover:bg-gray-100 transition-colors text-center">
                  <div className="flex justify-center mb-4">
                    <feature.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <footer className="bg-white border-t border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 mb-8">
            <div className="flex flex-col md:flex-row gap-4 md:gap-8">
              <Link to="/faq" className="text-gray-600 hover:text-gray-900">FAQ</Link>
              <Link to="/about" className="text-gray-600 hover:text-gray-900">About Trademarking.ai</Link>
              <Link to="/terms" className="text-gray-600 hover:text-gray-900">Terms of Service</Link>
            </div>
          </div>
          
          <div className="text-center">
            <p className="text-lg text-gray-600 font-medium mb-4">
              <strong>COMING SOON:</strong> Generative domain and trademark names, optimized for risk and branding power
            </p>
            <div className="text-sm text-gray-500">
              Copyright © 2025 Sharpe Idea Studio
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}